/**
 * Live-flow onboarding step list — the CURRENT founder/invitee shape, derived
 * from the canonical machine (`./machine.ts`) so there is one source of
 * ordering truth. The machine models the founder flow
 * (`personal|company|essentials|activation`); this file re-expresses it for the
 * live screens by aliasing `activation` → `data-source` (the current screen id)
 * and appending the shared `complete` result screen (not a counted step).
 *
 * The legacy `welcome` bookend was removed in the founder-flow reshape (Task 3):
 * a fresh founder now enters directly at `personal` (the machine's first
 * founder step). Everything that needs "what step am I on / what's next"
 * derives from here; do not hand-roll a competing list.
 */
import { getFlow, type FounderStep } from './machine';

export type OnboardingStep = 'personal' | 'company' | 'essentials' | 'data-source' | 'complete';

/** Machine founder step id → live-flow id (only `activation` differs). */
const FOUNDER_STEP_TO_LEGACY: Record<FounderStep, OnboardingStep> = {
  personal: 'personal',
  company: 'company',
  essentials: 'essentials',
  activation: 'data-source',
};

export const STARTUP_ONBOARDING_STEP_IDS: OnboardingStep[] = [
  ...getFlow('founder').map((s) => FOUNDER_STEP_TO_LEGACY[s as FounderStep]),
  'complete',
];

// Invitees skip company/essentials/data-source; their in-`(onboarding)`-stack
// portion is just personal→complete. (The machine's `invited_member` flow also
// models the pre-stack invite-accept screen at `/invite/team`, which is outside
// this list because it lives outside the onboarding router group.)
export const INVITEE_ONBOARDING_STEP_IDS: OnboardingStep[] = ['personal', 'complete'];

export const STEP_LABELS: Record<OnboardingStep, string> = {
  personal: 'Your Profile',
  company: 'Your Company',
  essentials: 'Company Essentials',
  'data-source': 'Add Your Data',
  complete: 'All Set',
};

function listFor(isInvitee: boolean): OnboardingStep[] {
  return isInvitee ? INVITEE_ONBOARDING_STEP_IDS : STARTUP_ONBOARDING_STEP_IDS;
}

export function resolveResumeStep(persisted: string | null | undefined, isInvitee: boolean): OnboardingStep {
  const list = listFor(isInvitee);
  const found = list.find((s) => s === persisted);
  return found ?? list[0];
}

export function nextStep(current: OnboardingStep, isInvitee: boolean): OnboardingStep | null {
  const list = listFor(isInvitee);
  const idx = list.indexOf(current);
  if (idx === -1 || idx === list.length - 1) return null;
  return list[idx + 1];
}
